import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  Card,
  CardContent,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  Code as CodeIcon,
  TextFields as TextFieldsIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import integrationService from '../../services/integrationService'

interface TemplatePreviewProps {
  template: {
    id: string
    name: string
    channel: string // WHATSAPP, EMAIL, SMS
    content?: string
    variables: string[]
  }
  onClose?: () => void
}

const SAMPLE_DATA_MAP: Record<string, string> = {
  customer_name: 'John Doe',
  customer_first_name: 'John',
  order_id: 'ORD-2026-001',
  order_number: 'SO-001',
  order_total: '₹15,000.00',
  order_status: 'Shipped',
  tracking_number: 'DELH123456789',
  payment_link: 'https://pay.example.com/inv-123',
  invoice_number: 'INV-2026-001',
  company_name: 'ABC Company',
  support_email: 'support@example.com',
  support_phone: '+91 98765 43210',
  document_number: 'DOC-2026-001',
  document_date: '16 Jul 2026',
  document_type: 'Invoice',
  amount: '₹15,000.00',
  download_url: 'https://example.com/download/doc-123',
  supplier_name: 'ABC Suppliers Pvt Ltd',
  po_number: 'PO-2026-001',
  item_count: '5',
}

const getSampleValue = (variableName: string): string => SAMPLE_DATA_MAP[variableName] || `[${variableName}]`

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template, onClose }) => {
  const { enqueueSnackbar } = useSnackbar()
  const channel = (template.channel || '').toUpperCase()

  const [variableValues, setVariableValues] = useState<Record<string, string>>(
    template.variables.reduce((acc, variable) => {
      acc[variable] = getSampleValue(variable)
      return acc
    }, {} as Record<string, string>)
  )
  const [rendered, setRendered] = useState<Record<string, string>>({})
  const [missingVariables, setMissingVariables] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [emailTab, setEmailTab] = useState<'html' | 'text'>('html')
  const [testRecipient, setTestRecipient] = useState('')
  const [sendingTest, setSendingTest] = useState(false)

  const loadPreview = useCallback(async () => {
    setLoading(true)
    try {
      const result: any = await integrationService.previewTemplate(template.id, variableValues)
      setRendered(result.rendered || result.rendered_content || {})
      setMissingVariables(result.missing_variables || [])
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to render preview', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [template.id, variableValues, enqueueSnackbar])

  useEffect(() => {
    loadPreview()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id])

  const handleReset = () => {
    setVariableValues(
      template.variables.reduce((acc, variable) => {
        acc[variable] = getSampleValue(variable)
        return acc
      }, {} as Record<string, string>)
    )
  }

  const handleSendTest = async () => {
    if (!testRecipient.trim()) {
      enqueueSnackbar(channel === 'EMAIL' ? 'Enter a recipient email address' : 'Enter a recipient phone number', { variant: 'warning' })
      return
    }
    setSendingTest(true)
    try {
      const result: any = await integrationService.testTemplate(template.id, testRecipient, variableValues)
      if (result.success !== false) {
        enqueueSnackbar(result.message || 'Test message sent successfully', { variant: 'success' })
      } else {
        enqueueSnackbar(result.message || 'Test message failed to send', { variant: 'error' })
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.detail || 'Failed to send test message', { variant: 'error' })
    } finally {
      setSendingTest(false)
    }
  }

  const getTemplateIcon = () => {
    switch (channel) {
      case 'WHATSAPP':
        return <MessageIcon sx={{ fontSize: 40, color: '#25D366' }} />
      case 'EMAIL':
        return <EmailIcon sx={{ fontSize: 40, color: '#1976d2' }} />
      case 'SMS':
        return <SmsIcon sx={{ fontSize: 40, color: '#ff9800' }} />
      default:
        return <MessageIcon sx={{ fontSize: 40 }} />
    }
  }

  const textBody = rendered.body || rendered.message || rendered.content || ''
  const htmlBody = rendered.html_body || ''
  const subject = rendered.subject || ''

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Template Preview: {template.name}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Rendered server-side with sample data — this is exactly what would be sent
      </Typography>

      <Grid container spacing={3}>
        {/* Variable Input Section */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Sample Data
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" startIcon={<RefreshIcon />} onClick={handleReset}>
                  Reset
                </Button>
                <Button size="small" variant="contained" onClick={loadPreview} disabled={loading}>
                  {loading ? <CircularProgress size={16} /> : 'Update Preview'}
                </Button>
              </Box>
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Edit the values below, then click Update Preview
            </Typography>

            <Box sx={{ mt: 2 }}>
              {template.variables.length === 0 ? (
                <Alert severity="info">
                  This template has no variables
                </Alert>
              ) : (
                template.variables.map((variable) => (
                  <TextField
                    key={variable}
                    fullWidth
                    label={variable}
                    value={variableValues[variable] || ''}
                    onChange={(e) =>
                      setVariableValues({
                        ...variableValues,
                        [variable]: e.target.value
                      })
                    }
                    sx={{ mb: 2 }}
                    size="small"
                    error={missingVariables.includes(variable)}
                    helperText={missingVariables.includes(variable) ? 'Missing — required by template' : undefined}
                  />
                ))
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Send a real test message
            </Typography>
            <TextField
              fullWidth
              size="small"
              label={channel === 'EMAIL' ? 'Recipient email address' : 'Recipient phone number'}
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <Button
              fullWidth
              variant="outlined"
              startIcon={sendingTest ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSendTest}
              disabled={sendingTest}
            >
              {sendingTest ? 'Sending...' : 'Send Test'}
            </Button>
          </Paper>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {getTemplateIcon()}
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  {channel || 'MESSAGE'}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  This is how your message will appear
                </Typography>
              </Box>
            </Box>

            {channel === 'EMAIL' ? (
              <>
                {subject && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">Subject</Typography>
                    <Typography variant="body1" fontWeight={600}>{subject}</Typography>
                  </Box>
                )}
                <Tabs value={emailTab} onChange={(_, v) => setEmailTab(v)} sx={{ mb: 1.5, minHeight: 36 }}>
                  <Tab
                    value="html"
                    label="HTML"
                    icon={<CodeIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 36 }}
                    disabled={!htmlBody}
                  />
                  <Tab
                    value="text"
                    label="Plain Text"
                    icon={<TextFieldsIcon fontSize="small" />}
                    iconPosition="start"
                    sx={{ minHeight: 36 }}
                  />
                </Tabs>

                {emailTab === 'html' ? (
                  htmlBody ? (
                    <Card variant="outlined">
                      <iframe
                        title="HTML email preview"
                        srcDoc={htmlBody}
                        sandbox=""
                        style={{ width: '100%', height: 380, border: 'none', display: 'block' }}
                      />
                    </Card>
                  ) : (
                    <Alert severity="info">
                      This template has no HTML variant — it will be sent as plain text only.
                    </Alert>
                  )
                ) : (
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          minHeight: 200,
                          whiteSpace: 'pre-wrap',
                          fontSize: '14px',
                        }}
                      >
                        {textBody}
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      minHeight: 200,
                      whiteSpace: 'pre-wrap',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                    }}
                  >
                    {textBody}
                  </Box>

                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="textSecondary">
                      Character count: {textBody.length}
                    </Typography>
                    {channel === 'SMS' && (
                      <Typography
                        variant="caption"
                        color={textBody.length > 160 ? 'error' : 'textSecondary'}
                      >
                        {textBody.length > 160
                          ? `${Math.ceil(textBody.length / 160)} SMS segments`
                          : 'Single SMS'}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {missingVariables.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Missing required variables: {missingVariables.map((v) => (
                  <Chip key={v} label={v} size="small" sx={{ ml: 0.5 }} />
                ))}
              </Alert>
            )}

            {channel === 'SMS' && textBody.length > 160 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                SMS messages longer than 160 characters will be sent as multiple segments,
                which may increase costs.
              </Alert>
            )}

            {channel === 'WHATSAPP' && textBody.length > 1000 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                WhatsApp messages longer than 1000 characters may be truncated.
              </Alert>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Actions */}
      {onClose && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onClose}>
            Close Preview
          </Button>
        </Box>
      )}
    </Box>
  )
}

export default TemplatePreview
